import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import styles from "./Leaderboard.module.css";
import LoadingSpinner from "../components/LoadingSpinner"; // Import the spinner

//Updated Avatar Color Palette and Hashing
const AVATAR_COLORS = [
    '#4299E1',
    '#48BB78',
    '#ECC94B',
    '#ED8936',
    '#9F7AEA',
    '#E53E3E',
    '#38B2AC',
    '#ED64A6',
    '#A0AEC0',
    '#667EEA',
    '#10B981',
    '#F59E0B',
    '#6366F1',
    '#EF4444',
    '#14B8A6',
    '#EC4899',
    '#9CA3AF',
    '#3B82F6',
    '#06B6D4',
    '#F472B6',
    '#A78BFA',
    '#D97706',
    '#DC2626',
    '#84CC16',
    '#22C55E',
    '#0EA5E9',
];


const getDeterministicColor = (username) => {
    let hash = 5381;
    for (let i = 0; i < username.length; i++) {
        hash = (hash * 33) ^ username.charCodeAt(i);
    }
    const index = Math.abs(hash) % AVATAR_COLORS.length;
    return AVATAR_COLORS[index];
};

function Leaderboard() {
    const { user } = useAuth();
    const [leaders, setLeaders] = useState([]);
    const [period, setPeriod] = useState("all");
    const [activity, setActivity] = useState("all");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!user) {
            setLeaders([]);
            setLoading(false);
            setError("");
            return;
        }

        const fetchLeaderboard = async () => {
            setLoading(true); // Start loading when fetch begins
            setError("");
            try {
                const res = await axios.get(
                    `https://deenroute-backend-908197786741.africa-south1.run.app/api/scores/leaderboard?period=${period}&activity=${activity}`
                );
                setLeaders(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                setError("Failed to load leaderboard.");
                console.error("Error fetching leaderboard:", err.response ? err.response.data : err.message, err);
                setLeaders([]);
            }
            setLoading(false); // Stop loading after fetch completes (success or failure)
        };

        fetchLeaderboard();
    }, [period, activity, user]);

    const handlePeriodChange = (e) => {
        setPeriod(e.target.value);
    };

    const handleActivityChange = (e) => {
        setActivity(e.target.value);
    };

    // Define background style here so it's available for the loading state
    const backgroundStyle = {
        backgroundImage: `url("/images/leaderboardimage.jpg")`,
    };

    return (
        <div
            className={styles.background}
            style={backgroundStyle}
        >
            <div className={styles.overlay}></div>
            <div className={styles.container}>
                <h2 className={styles.title}>🏆 Leaderboard</h2>

                {!user ? (
                    <div className={styles.instructionsPopup}>
                        <h3>🚫 Access Restricted</h3>
                        <p>Please login or sign up to access this feature.</p>
                        <button
                            className={styles.proceedButton}
                            onClick={() => window.location.href = "/login"}
                        >
                            Go to Login
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: "1rem", textAlign: "center", display: "flex", justifyContent: "center", gap: "1rem", flexWrap: "wrap" }}>
                            <div>
                                <label htmlFor="period" style={{ marginRight: "0.5rem" }}>
                                    Filter by Time:
                                </label>
                                <select id="period" value={period} onChange={handlePeriodChange}>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="all">All time</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="activity" style={{ marginRight: "0.5rem" }}>
                                    Filter by Activity:
                                </label>
                                <select id="activity" value={activity} onChange={handleActivityChange}>
                                    <option value="all">All Activities</option>
                                    <option value="originalQuiz">Quiz</option>
                                    <option value="halalVsHaram">Halal or Haram</option>
                                    <option value="fillInTheBlanks">Fill in the Blanks</option>
                                    <option value="flashcards">Flashcards</option>
                                </select>
                            </div>
                        </div>

                        {/* Render the spinner while loading */}
                        {loading ? (
                            <LoadingSpinner />
                        ) : error ? (
                            <p className={styles.message} style={{ color: "red" }}>{error}</p>
                        ) : leaders.length === 0 ? (
                            <p className={styles.message}>No scores yet for this selection. Be the first to play!</p>
                        ) : (
                            <ul className={styles.leaderboardList}>
                                {leaders.map((leader, index) => {
                                    const isCurrentUser = user && leader.username === user.username;

                                    const avatarBackgroundColor = getDeterministicColor(leader.username);

                                    return (
                                        <li
                                            key={leader._id || index}
                                            className={`${styles.leaderboardItem} ${
                                                isCurrentUser ? styles.highlight : ""
                                            }`}
                                        >
                                            <span className={styles.rank}>{index + 1}</span>

                                            <div
                                                className={styles.avatar}
                                                style={{ backgroundColor: avatarBackgroundColor }}
                                            >
                                                <span role="img" aria-label="user icon">👤</span>
                                            </div>
                                            <span className={styles.username}>{leader.username}</span>
                                            <span className={styles.score}>{leader.totalScore}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default Leaderboard;
